import Link from 'next/link';
import { useRouter } from 'next/router';
import { ChevronRight } from 'lucide-react';
import { useI18n } from '../context/I18nContext';
import ImageOnlySkeletonLoader from './ImageOnlySkeletonLoader';

function splitTitleParts(text = '') {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return { line1: String(text || '').trim(), line2: '' };
  return {
    line1: words.slice(0, -1).join(' '),
    line2: words[words.length - 1],
  };
}

function normalizePath(pathname = '') {
  if (pathname.startsWith('/machines')) return '/machines';
  if (pathname.startsWith('/spare-parts')) return '/spare-parts';
  return pathname;
}

export default function ShortcutsSection({
  titleKey = 'aboutPage.shortcuts.title',
  titleLine1Key = 'aboutPage.shortcuts.titleLine1',
  titleLine2Key = 'aboutPage.shortcuts.titleLine2',
  items = [],
  excludeCurrent = true,
}) {
  const { t } = useI18n();
  const router = useRouter();
  const currentPath = normalizePath(router.pathname || '');

  const line1 = String(t(titleLine1Key, '') || '').trim();
  const line2 = String(t(titleLine2Key, '') || '').trim();
  const titleParts = line1 || line2
    ? { line1: line1 || String(t(titleKey) || '').trim(), line2 }
    : splitTitleParts(t(titleKey));

  const visibleItems = (excludeCurrent
    ? items.filter((item) => normalizePath(item.href) !== currentPath)
    : items
  ).slice(0, 3);

  return (
    <section className="relative overflow-hidden bg-[var(--dash-other)] pt-12">
      <div className="pointer-events-none absolute inset-0 mix-blend-overlay bg-[linear-gradient(140deg,rgba(0,0,0,0.65)_0%,rgba(0,0,0,0)_100%)]" />
      <div className="container relative z-10 mx-auto px-6 lg:px-10">
        <div className="mb-10">
          <h2 className="text-3xl font-light uppercase leading-tight text-white sm:text-4xl">
            <span>{titleParts.line1}</span>
            {titleParts.line2 ? <span className="font-black text-white"> {titleParts.line2}</span> : null}
          </h2>
        </div>
      </div>

      <div className="relative z-10 w-full">
        <div className="grid gap-0 md:grid-cols-3">
          {visibleItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="group relative flex min-h-[280px] flex-col justify-end overflow-hidden p-8 transition-all duration-300 hover:border-[var(--dash-main)]/60 sm:p-10"
            >
              <ImageOnlySkeletonLoader
                src={item.image}
                mode="native"
                alt={t(item.titleKey)}
                containerClassName="absolute inset-0"
                imgClassName="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-x-0 bottom-0 h-full bg-black/45 opacity-0 transition-all duration-700 group-hover:opacity-100" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 transition-all duration-500 group-hover:h-full bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1 w-0 bg-[var(--dash-main)] transition-all duration-500 group-hover:w-full" />
              <div className="relative z-10 flex items-center justify-between translate-y-10 transition-all duration-500 group-hover:-translate-y-1">
                <p className="flex items-center gap-3 text-2xl font-bold text-white">
                  <span className="h-2 w-2 rounded-sm bg-[var(--dash-other)]" />
                  {t(item.titleKey)}
                </p>
                <div className="flex items-center text-white/80">
                  <ChevronRight className="h-5 w-5 -mr-3 opacity-40 transition-transform duration-500 group-hover:translate-x-1 group-hover:animate-[chevronFlash_1.4s_linear_infinite]" />
                  <ChevronRight className="h-5 w-5 -mr-3 opacity-25 transition-transform duration-500 group-hover:translate-x-2 group-hover:animate-[chevronFlash_1.4s_linear_infinite] group-hover:[animation-delay:0.2s]" />
                  <ChevronRight className="h-5 w-5 opacity-15 transition-transform duration-500 group-hover:translate-x-3 group-hover:animate-[chevronFlash_1.4s_linear_infinite] group-hover:[animation-delay:0.4s]" />
                </div>
              </div>
              <p className="relative z-10 mt-3 min-h-[2.75rem] translate-y-4 text-sm leading-relaxed text-slate-400 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:text-white group-hover:opacity-100">
                {t(item.descriptionKey)}
              </p>
            </Link>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes chevronFlash {
          0% {
            opacity: 0.2;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.2;
          }
        }
      `}</style>
    </section>
  );
}
